// App shell: owns which tab is active and the cross-cutting data
// (balance + orders) that needs loading once at startup. Pages
// stay mounted simultaneously so switching tabs is a cross-fade,
// not a remount — this is what keeps scroll position and in-flight
// animations intact when someone bounces between tabs.

import { useEffect, useState } from 'react';
import { TopBar } from '@/components/TopBar';
import { TabBar } from '@/components/TabBar';
import { MarketPage } from '@/pages/Market';
import { OrdersPage } from '@/pages/Orders';
import { ProfilePage } from '@/pages/Profile';
import { useAppStore } from '@/store/AppStore';
import { useTelegram } from '@/hooks/useTelegram';
import { api } from '@/services/api';
import { cx } from '@/utils/cx';
import { OverlayPortalProvider } from '@/providers/OverlayPortalProvider';
import type { TabKey } from '@/constants/tabs';
import styles from './App.module.scss';

export function App() {
  const [activeTab, setActiveTab] = useState<TabKey>('market');
  const { state, dispatch } = useAppStore();
  const { user } = useTelegram();

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'SET_ORDERS_LOADING', payload: true });
    Promise.all([api.getOrders(), api.getBalance()])
      .then(([orders, balance]) => {
        if (cancelled) return;
        dispatch({ type: 'SET_ORDERS', payload: orders });
        dispatch({ type: 'SET_BALANCE', payload: balance });
      })
      .finally(() => {
        if (!cancelled) dispatch({ type: 'SET_ORDERS_LOADING', payload: false });
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (user) dispatch({ type: 'SET_USER', payload: user });
  }, [user, dispatch]);

  return (
    <div className={styles.app}>
      <div className={styles.bgGlow} aria-hidden="true" />
      <OverlayPortalProvider>
        <TopBar balance={state.balanceStars} />

        <main className={styles.panels}>
          <div className={cx(styles.panel, activeTab === 'market' && styles.active)}>
            <MarketPage />
          </div>
          <div className={cx(styles.panel, activeTab === 'orders' && styles.active)}>
            <OrdersPage onNavigate={setActiveTab} />
          </div>
          <div className={cx(styles.panel, activeTab === 'profile' && styles.active)}>
            <ProfilePage />
          </div>
        </main>

        <TabBar active={activeTab} onChange={setActiveTab} />
      </OverlayPortalProvider>
    </div>
  );
}
